import { Observable, Subscription } from 'rxjs';
import { ApiResponse } from '../types';
import type {
  AddMemberResult,
  AddMembersData,
  AddMembersPayload,
  ExistingMemberCandidate,
  FamilyAddContext,
  FamilyMemberDraftItem,
  FamilyOtpSent,
} from '../types/family-members.types';
import type { FamilyStartSubject } from '../types/family-start.types';
import {
  answeredByServer,
  familyMemberErrorMessage,
  otpActionFor,
  seatsLeft,
} from './family-members.util';
import { startFamilyReasonFromError } from './family-start.util';

/** The calls the add-members flow makes; implemented by FamilyMembersService. */
export interface FamilyMembersActions {
  context(groupId: string, lprEntryId?: string): Observable<ApiResponse<FamilyAddContext>>;
  searchExisting(
    groupId: string,
    query: string
  ): Observable<ApiResponse<ExistingMemberCandidate[]>>;
  sendOtp(
    groupId: string,
    action: 'add_plate' | 'add_member'
  ): Observable<ApiResponse<FamilyOtpSent>>;
  addMembers(groupId: string, payload: AddMembersPayload): Observable<ApiResponse<AddMembersData>>;
}

export interface FamilyMembersFlowOptions {
  /** Something was added: reload the lists behind the pop-up. */
  onChanged?: () => void;
}

export type FamilyMembersStep = 'add' | 'otp' | 'done';

/** One row of the done step: what was lined up and what happened to it. */
export interface FamilyMemberOutcome {
  label: string;
  status: 'added' | 'failed';
  message: string;
}

const MIN_QUERY_LENGTH = 3;
/** Refusals of the code itself: the attendant fixes the code, not the members. */
const OTP_CODES = new Set([
  'OTP_REQUIRED',
  'OTP_INVALID',
  'OTP_TOO_MANY_ATTEMPTS',
  'OTP_UNAVAILABLE',
]);

/**
 * The state behind the "add family members" pop-up that follows Start Family (CWS-1581): the
 * context, the members lined up, the primary's code and the results. One request runs at a time.
 * Plain fields, so it works with default (Eager) change detection. Call `destroy()` from the
 * host's `ngOnDestroy`.
 */
export class FamilyMembersFlowController {
  isOpen = false;
  step: FamilyMembersStep = 'add';
  groupId: string | null = null;
  subject: FamilyStartSubject | null = null;
  /** True right after Start Family; false when members are added to a family that already runs. */
  justStarted = false;
  context: FamilyAddContext | null = null;
  loading = false;
  loadError = '';

  drafts: FamilyMemberDraftItem[] = [];
  candidates: ExistingMemberCandidate[] = [];
  searching = false;
  searched = false;

  /**
   * Keys every charge of one add on the server, so it is kept for a retry after a lost answer
   * (a second try must not charge again) and dropped when the members change or the server answers.
   */
  private requestId: string | null = null;

  busy = false;
  /** A refusal of the add itself (not of the code). */
  error = '';
  otpSending = false;
  otpSent = false;
  otpPhoneLast4: string | null = null;
  otpCode = '';
  otpError = '';

  outcomes: FamilyMemberOutcome[] = [];

  private lprEntryId: string | undefined;
  private submitted: FamilyMemberDraftItem[] = [];
  private inFlight = new Subscription();
  private searchRequest: Subscription | null = null;

  constructor(
    private readonly actions: FamilyMembersActions,
    private readonly options: FamilyMembersFlowOptions = {}
  ) {}

  /** Opens the add step for a family and loads what it can offer. */
  open(groupId: string, subject: FamilyStartSubject, justStarted = false): void {
    this.reset();
    this.isOpen = true;
    this.groupId = groupId;
    this.subject = subject;
    this.justStarted = justStarted;
    this.lprEntryId = subject.lprEntryId?.trim() || undefined;
    this.loading = true;
    this.inFlight.add(
      this.actions.context(groupId, this.lprEntryId).subscribe({
        next: response => {
          this.loading = false;
          this.context = response.data ?? null;
        },
        error: error => {
          this.loading = false;
          this.loadError = this.messageOf(error);
        },
      })
    );
  }

  close(): void {
    if (this.busy) {
      return;
    }
    this.reset();
  }

  get seatsLeft(): number {
    return this.context ? seatsLeft(this.context, this.drafts.length) : 0;
  }

  /** New vehicles are charged to the primary's saved card, so without one only members can join. */
  get canAddNewVehicle(): boolean {
    return (!!this.context?.card || !!this.context?.card_unavailable) && this.seatsLeft > 0;
  }

  get canAddExisting(): boolean {
    return this.seatsLeft > 0;
  }

  addDraft(item: FamilyMemberDraftItem): boolean {
    if (this.seatsLeft < 1 || this.busy) {
      return false;
    }
    this.drafts = [...this.drafts, item];
    this.requestId = null;
    this.error = '';
    return true;
  }

  removeDraft(localId: string): void {
    if (!this.busy) {
      this.drafts = this.drafts.filter(item => item.localId !== localId);
      this.requestId = null;
    }
  }

  search(query: string): void {
    const text = (query || '').trim();
    // A slower earlier search must not replace the results of this one.
    this.searchRequest?.unsubscribe();
    this.searching = false;
    this.candidates = [];
    this.searched = false;
    if (!this.groupId || text.length < MIN_QUERY_LENGTH) {
      return;
    }
    this.searching = true;
    this.searchRequest = this.actions.searchExisting(this.groupId, text).subscribe({
      next: response => {
        this.searching = false;
        this.searched = true;
        // A member already lined up is not offered twice.
        const lined = new Set(
          this.drafts.flatMap(item =>
            item.payload.kind === 'existing' ? [item.payload.subscription_id] : []
          )
        );
        this.candidates = (response.data ?? []).filter(
          candidate => !lined.has(candidate.subscription_id)
        );
      },
      error: error => {
        this.searching = false;
        this.searched = true;
        this.error = this.messageOf(error);
      },
    });
    this.inFlight.add(this.searchRequest);
  }

  clearCandidates(): void {
    this.candidates = [];
    this.searched = false;
  }

  /** Add members: asks for the primary's code first unless the primary's car is at the lane. */
  proceed(): void {
    if (!this.drafts.length || this.busy) {
      return;
    }
    this.error = '';
    if (this.context?.otp_required) {
      this.step = 'otp';
      this.otpCode = '';
      this.otpError = '';
      if (!this.otpSent) {
        this.sendOtp();
      }
      return;
    }
    this.submit();
  }

  backToAdd(): void {
    if (!this.busy) {
      this.step = 'add';
    }
  }

  sendOtp(): void {
    if (!this.groupId || this.otpSending) {
      return;
    }
    this.otpSending = true;
    this.otpError = '';
    this.inFlight.add(
      this.actions.sendOtp(this.groupId, otpActionFor(this.drafts)).subscribe({
        next: response => {
          this.otpSending = false;
          this.otpSent = true;
          this.otpPhoneLast4 =
            response.data?.phone_last4 ?? this.context?.primary_phone_last4 ?? null;
        },
        error: error => {
          this.otpSending = false;
          this.otpError = this.messageOf(error);
        },
      })
    );
  }

  /** The code is exactly six digits. */
  get canSubmitCode(): boolean {
    return /^\d{6}$/.test(this.otpCode.trim()) && !this.busy;
  }

  submit(): void {
    if (!this.groupId || !this.drafts.length || this.busy) {
      return;
    }
    const code = this.otpCode.trim();
    if (this.context?.otp_required && !/^\d{6}$/.test(code)) {
      this.otpError = familyMemberErrorMessage('OTP_REQUIRED', '');
      return;
    }
    this.busy = true;
    this.error = '';
    this.otpError = '';
    const submitted = this.drafts;
    this.inFlight.add(
      this.actions
        .addMembers(this.groupId, {
          // The same id until the server answers: a retry after a lost response is not charged twice.
          request_id: (this.requestId ??= crypto.randomUUID()),
          members: submitted.map(item => item.payload),
          // Sent whenever a code was typed: a scan that went stale after the pop-up opened still
          // needs it, and the server ignores it when the scan stands in.
          ...(/^\d{6}$/.test(code) ? { otp_code: code } : {}),
          ...(this.lprEntryId ? { lpr_entry_id: this.lprEntryId } : {}),
        })
        .subscribe({
          next: response => {
            this.busy = false;
            this.requestId = null;
            this.submitted = submitted;
            this.outcomes = this.toOutcomes(submitted, response.data?.results ?? []);
            this.step = 'done';
            this.otpSent = false;
            this.drafts = [];
            if (this.outcomes.some(outcome => outcome.status === 'added')) {
              this.options.onChanged?.();
            }
          },
          error: error => {
            this.busy = false;
            // An answer from the server is final for this id (a decline would replay on a retry);
            // no answer (offline, timeout, a gateway error) keeps it so the retry cannot double charge.
            if (answeredByServer(error)) {
              this.requestId = null;
            }
            const reason = startFamilyReasonFromError(error);
            const message = familyMemberErrorMessage(reason.code, reason.message);
            if (OTP_CODES.has(reason.code)) {
              this.step = 'otp';
              this.otpError = message;
              if (reason.code === 'OTP_REQUIRED' || reason.code === 'OTP_TOO_MANY_ATTEMPTS') {
                this.otpSent = false;
              }
              if (reason.code === 'OTP_REQUIRED' && this.context && !this.context.otp_required) {
                // The scan that stood in for the code went stale (the add waited too long): from
                // here on the primary's code is needed, so send one instead of looping.
                this.context = { ...this.context, otp_required: true };
                this.otpCode = '';
                this.sendOtp();
              }
            } else {
              this.step = 'add';
              this.error = message;
            }
          },
        })
    );
  }

  /** Puts the members that failed back in the list so they can be tried again (needs a new code). */
  retryFailed(): void {
    if (this.busy) {
      return;
    }
    const failed = new Set(
      this.outcomes
        .map((outcome, index) => (outcome.status === 'failed' ? index : -1))
        .filter(index => index >= 0)
    );
    this.drafts = this.submitted.filter((_item, index) => failed.has(index));
    this.requestId = null;
    this.outcomes = [];
    this.otpCode = '';
    this.otpSent = false;
    this.step = 'add';
  }

  get anyFailed(): boolean {
    return this.outcomes.some(outcome => outcome.status === 'failed');
  }

  destroy(): void {
    this.inFlight.unsubscribe();
    this.inFlight = new Subscription();
  }

  private toOutcomes(
    submitted: FamilyMemberDraftItem[],
    results: AddMemberResult[]
  ): FamilyMemberOutcome[] {
    return submitted.map((item, index) => {
      const result = results.find(row => row.index === index);
      if (!result) {
        return { label: item.label, status: 'failed', message: 'No answer for this member.' };
      }
      return {
        label: item.label,
        status: result.status,
        message:
          result.status === 'added'
            ? this.addedMessage(item, result)
            : familyMemberErrorMessage(
                result.code,
                result.message ?? 'This member could not be added.'
              ),
      };
    });
  }

  private addedMessage(item: FamilyMemberDraftItem, result: AddMemberResult): string {
    const charged = result.charged ?? item.chargeNow;
    return charged > 0
      ? `Charged $${charged.toFixed(2)} to the primary's card.`
      : 'Added. The discount applies from the next renewal.';
  }

  private messageOf(error: unknown): string {
    const reason = startFamilyReasonFromError(error);
    return familyMemberErrorMessage(reason.code, reason.message);
  }

  private reset(): void {
    this.inFlight.unsubscribe();
    this.inFlight = new Subscription();
    this.isOpen = false;
    this.step = 'add';
    this.groupId = null;
    this.subject = null;
    this.justStarted = false;
    this.context = null;
    this.loading = false;
    this.loadError = '';
    this.drafts = [];
    this.requestId = null;
    this.candidates = [];
    this.searching = false;
    this.searched = false;
    this.busy = false;
    this.error = '';
    this.otpSending = false;
    this.otpSent = false;
    this.otpPhoneLast4 = null;
    this.otpCode = '';
    this.otpError = '';
    this.outcomes = [];
    this.submitted = [];
    this.lprEntryId = undefined;
  }
}
